import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import ContactForm from '../ContactFrom/ContactForm'
import icon1 from '../../images/icons/icon_map_mark_2.svg'
import icon2 from '../../images/icons/icon_calling_2.svg'
import icon3 from '../../images/icons/icon_mail_3.svg'

const offices = [
    {
        id: 'malaysia-head-office',
        highlightIds: ['malaysia'],
        country: 'Malaysia',
        label: 'Head Office',
        address: 'Unit 18-3, Jalan 14/48A, 51100 Sentul, Kuala Lumpur, Malaysia',
        note: 'Main branch for business enquiries, project consultation and delivery coordination.',
    },
    {
        id: 'malaysia-branch-office',
        highlightIds: [],
        country: 'Malaysia',
        label: 'City Center Office',
        address: 'Suite 5.33, 5th Floor, Wisma Central, No. 147, Jalan Ampang, 50450 Kuala Lumpur, Malaysia',
        note: 'Branch office for client meetings, business support and delivery coordination.',
    },
    {
        id: 'india',
        highlightIds: ['india'],
        country: 'India',
        label: 'India Office',
        address: '#189, Ground Floor, 4th Main, HAL 3rd Stage, New Thippasandra, Bengaluru - 560075, India',
        note: 'Regional office supporting technology delivery and collaboration.',
    },
    {
        id: 'australia',
        highlightIds: ['australia'],
        country: 'Australia',
        label: 'Australia Office',
        address: '186 Honeywood Ave, Wandi, Western Australia, PIN: 6167',
        note: 'Regional contact point for Australia-based clients and project coordination. Mobile: 0406 183 393.',
    },
    {
        id: 'singapore',
        highlightIds: ['singapore'],
        country: 'Singapore',
        label: 'Service Region',
        address: '721, north bridge road. Spore 198689',
        note: 'Serving Singapore businesses through remote consulting, cloud support and delivery coordination.',
    },
    {
        id: 'uae',
        highlightIds: ['uae'],
        country: 'UAE',
        label: 'Service Region',
        address: 'United Arab Emirates client support and project coordination',
        note: 'Supporting UAE clients with IT services, software delivery and continuous technical assistance.',
    },
    {
        id: 'ksa',
        highlightIds: ['ksa'],
        country: 'Saudi Arabia',
        label: 'Jeddah Office',
        address: 'Building No. 123 Sulaiman Razi, Room No. 43, First Floor, Shari Bawa, Box No. 1645, Jeddah, Saudi Arabia',
        note: 'Saudi Arabia regional office for client support and coordination. Mobile: 00966 565508085.',
    },
];

const defaultMapOffice = {
    country: 'Malaysia',
    label: 'Head Office & City Center Office',
    address: `${offices[0].address} to ${offices[1].address}`,
    mapSrc: `https://maps.google.com/maps?saddr=${encodeURIComponent(offices[0].address)}&daddr=${encodeURIComponent(offices[1].address)}&z=13&output=embed`,
};

const contactMethods = [
    {
        icon: icon2,
        title: 'Phone',
        lines: ['+603-6242 7299'],
    },
    {
        icon: icon3,
        title: 'Email',
        lines: ['sales@krit-tech.com', 'support@krit-tech.com'],
    },
    {
        icon: icon2,
        title: 'Support',
        lines: ['Worldwide client support', 'Continuous project assistance'],
    },
];

const ContactSection = () => {
    const { hash } = useLocation();
    const activeRegion = useMemo(() => hash.replace('#office-', '').toLowerCase(), [hash]);
    const [selectedMapOffice, setSelectedMapOffice] = useState(defaultMapOffice);
    const mapSrc = selectedMapOffice.mapSrc || `https://maps.google.com/maps?q=${encodeURIComponent(selectedMapOffice.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

    useEffect(() => {
        if (!activeRegion) {
            return;
        }

        const office = offices.find((item) => item.highlightIds.includes(activeRegion));
        const target = office ? document.getElementById(`office-${office.id}`) : null;

        if (target) {
            setSelectedMapOffice(office);
            setTimeout(() => {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
        }
    }, [activeRegion]);

    return (
        <section className="contact_section section_space bg-light">
            <div className="container">
                <div className="contact_intro_block">
                    <div className="heading_block mb-0">
                        <div className="heading_focus_text">
                            Global Presence
                            <span className="badge bg-secondary text-white">Multiple Regional Offices</span>
                        </div>
                        <h2 className="heading_text mb-0">
                            Contact Krit Cloud Platforms Sdn Bhd
                        </h2>
                    </div>
                    <p className="mb-0">
                        Reach our Malaysia, India, Australia and Saudi Arabia offices, or connect with our regional service coverage for Singapore and UAE. Our work is not limited by business hours because we support clients across regions.
                    </p>
                </div>

                <div className="row office_location_grid">
                    {offices.map((office) => (
                        <div className="col-lg-4 col-md-6" key={office.id}>
                            <div
                                id={`office-${office.id}`}
                                className={`office_location_card ${office.label === 'Head Office' ? 'is_head_office' : ''} ${office.highlightIds.includes(activeRegion) ? 'is_highlighted' : ''}`}
                            >
                                <div className="office_card_header">
                                    <div className="office_icon">
                                        <img src={icon1} alt={`${office.country} office location`} />
                                    </div>
                                    <div>
                                        <span className="office_label">{office.label}</span>
                                        <h3>{office.country}</h3>
                                    </div>
                                </div>
                                <p className="office_address">{office.address}</p>
                                <p className="office_note">{office.note}</p>
                                <button
                                    type="button"
                                    className="office_map_btn"
                                    onClick={() => setSelectedMapOffice(office)}
                                >
                                    <i className="fa-solid fa-location-dot"></i>
                                    See in Map
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="contact_methods_row">
                    {contactMethods.map((method) => (
                        <div className="contact_method_item" key={method.title}>
                            <div className="contact_method_icon">
                                <img src={method.icon} alt={`${method.title} icon`} />
                            </div>
                            <div>
                                <h3>{method.title}</h3>
                                {method.lines.map((line) => (
                                    <p className="mb-0" key={line}>{line}</p>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="contact_form_map_block">
                    <div className="row justify-content-lg-between g-4">
                        <div className="col-lg-7">
                            <div className="contact_form mb-0">
                                <h3 className="details_item_info_title mb-1">Send Us A Message</h3>
                                <p className="mb-5">
                                    Tell us what you want to build, improve or modernize. Our team will get back to you with the next practical step.
                                </p>
                                <ContactForm />
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="main_office_map">
                                <div className="map_heading">
                                    <span>Selected Location Map</span>
                                    <h3>{selectedMapOffice.country} - {selectedMapOffice.label}</h3>
                                </div>
                                <div className="gmap_canvas">
                                    <iframe
                                        title={`Krit Cloud Platforms ${selectedMapOffice.country} map`}
                                        src={mapSrc}
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ContactSection;
